'use client';

import Link from 'next/link';

import type { PostMention } from '@kit/community';

const URL_PATTERN = /https?:\/\/[^\s<>"')\]]+/g;
// Unicode-aware hashtag: '#' followed by letters/marks/digits/underscore.
const HASHTAG_PATTERN = /#[\p{L}\p{M}0-9_]+/gu;
const MAX_DISPLAY_LENGTH = 40;

function displayUrl(url: string): string {
  const stripped = url.replace(/^https?:\/\//, '').replace(/\/$/, '');
  return stripped.length > MAX_DISPLAY_LENGTH
    ? `${stripped.slice(0, MAX_DISPLAY_LENGTH)}…`
    : stripped;
}

/** Destination for a resolved mention. Profiles live at the app root
 * (`/{handle}`), organizations under the community tree. Exported for tests. */
export function mentionHref(mention: PostMention): string | null {
  if (!mention.username) return null;
  return mention.type === 'organization'
    ? `/home/community/organizations/${mention.username}`
    : `/${mention.username}`;
}

interface Segment {
  start: number;
  end: number;
  kind: 'url' | 'mention' | 'hashtag';
  mention?: PostMention;
}

/** True if [start,end) overlaps any already-claimed segment. */
function overlaps(segments: Segment[], start: number, end: number): boolean {
  return segments.some((s) => start < s.end && end > s.start);
}

/**
 * Build non-overlapping, ordered segments for the linkable spans in a body.
 * Priority: stored mentions (explicit offsets) → URLs → hashtags. A later
 * candidate that overlaps an already-claimed span is dropped.
 *
 * Exported for unit testing (the branch-driving logic of this module).
 */
export function buildSegments(
  body: string,
  mentions: PostMention[],
): Segment[] {
  const segments: Segment[] = [];

  // 1. Stored mentions win. Validate the offset still points at an '@' span
  //    (bodies can be edited after mentions were captured) and resolves.
  for (const mention of mentions) {
    const { start, end } = mention;
    if (
      typeof start !== 'number' ||
      typeof end !== 'number' ||
      start < 0 ||
      end > body.length ||
      start >= end
    ) {
      continue;
    }
    if (body[start] !== '@') continue;
    if (!mentionHref(mention)) continue;
    if (overlaps(segments, start, end)) continue;
    segments.push({ start, end, kind: 'mention', mention });
  }

  // 2. URLs.
  for (const match of body.matchAll(URL_PATTERN)) {
    const start = match.index;
    const end = start + match[0].length;
    if (overlaps(segments, start, end)) continue;
    segments.push({ start, end, kind: 'url' });
  }

  // 3. Hashtags.
  for (const match of body.matchAll(HASHTAG_PATTERN)) {
    const start = match.index;
    const end = start + match[0].length;
    if (overlaps(segments, start, end)) continue;
    segments.push({ start, end, kind: 'hashtag' });
  }

  return segments.sort((a, b) => a.start - b.start);
}

interface PostBodyProps {
  body: string;
  mentions?: PostMention[];
  className?: string;
}

/**
 * Renders post body text with URLs, resolved @mentions, and #hashtags as
 * links (X/Threads convention). URLs show without protocol and truncated.
 * Only mentions present in the post's stored `mentions` (i.e. resolved to a
 * real Ballee profile/org) become links — external handles stay plain text.
 */
export function PostBody({ body, mentions = [], className }: PostBodyProps) {
  const segments = buildSegments(body, mentions);
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;

  for (const segment of segments) {
    if (segment.start > lastIndex) {
      parts.push(body.slice(lastIndex, segment.start));
    }

    const raw = body.slice(segment.start, segment.end);
    const key = `${segment.kind}-${segment.start}`;

    if (segment.kind === 'url') {
      parts.push(
        <a
          key={key}
          href={raw}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary break-all hover:underline"
        >
          {displayUrl(raw)}
        </a>,
      );
    } else if (segment.kind === 'mention' && segment.mention) {
      parts.push(
        <Link
          key={key}
          href={mentionHref(segment.mention) as string}
          className="text-primary font-medium hover:underline"
        >
          {raw}
        </Link>,
      );
    } else if (segment.kind === 'hashtag') {
      const tag = raw.slice(1).toLowerCase();
      parts.push(
        <Link
          key={key}
          href={`/home/community/hashtag/${encodeURIComponent(tag)}`}
          className="text-primary hover:underline"
        >
          {raw}
        </Link>,
      );
    }

    lastIndex = segment.end;
  }

  if (lastIndex < body.length) {
    parts.push(body.slice(lastIndex));
  }

  return (
    <p
      className={`text-foreground text-[15px] leading-relaxed break-words whitespace-pre-wrap ${className ?? ''}`}
    >
      {parts}
    </p>
  );
}
